import React, { memo, useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import { collectUnmappedFields, isFieldActionable } from '../../formReview.utils';
import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../../FormReviewCard.constants';
import FormReviewSection from '../FormReviewSection';
import { schemaWarningStyles, surfaceContentStyles } from '../FormReviewSurface.styles';
import FormReviewUnmappedSection from '../FormReviewUnmappedSection';

const sortByOrder = items => _.sortBy(items ?? [], item => item.order ?? 0);

const FormReviewSurfaceContent = ({ additionalProps, setIsSavingAllowed }) => {
  const { form, onOpenDocument } = additionalProps;

  useEffect(() => {
    setIsSavingAllowed(false);
  }, [setIsSavingAllowed]);

  const fieldsByKey = useMemo(
    () => Object.fromEntries((form?.fields ?? []).map(field => [field.fieldKey, field])),
    [form?.fields],
  );

  const tableDiffsByKey = useMemo(
    () => Object.fromEntries((form?.tableDiffs ?? []).map(diff => [diff.fieldKey, diff])),
    [form?.tableDiffs],
  );

  const sections = useMemo(() => sortByOrder(form?.schema?.sections), [form?.schema?.sections]);

  const unmappedFields = useMemo(() => collectUnmappedFields(form), [form]);

  if (!form) {
    return null;
  }

  const isActionable = isFieldActionable(form.status);

  return (
    <Box data-testid={FormReviewTestIds.SURFACE} sx={surfaceContentStyles}>
      {form.schemaVersionMatches === false ? (
        <Box data-testid={FormReviewTestIds.SCHEMA_MISMATCH} sx={schemaWarningStyles}>
          <StatusIconCDS size={16} type={StatusEnum.WARNING} />
          <Typography color="warning.dark" variant="regular12">
            {FORM_REVIEW_LABELS.SCHEMA_MISMATCH}
          </Typography>
        </Box>
      ) : null}
      {sections.map(section => (
        <FormReviewSection
          isTriageSorted
          fieldsByKey={fieldsByKey}
          form={form}
          formId={form.id}
          isActionable={isActionable}
          isInFlight={additionalProps.isInFlight}
          key={section.key}
          section={section}
          tableDiffsByKey={tableDiffsByKey}
          onOpenDocument={onOpenDocument}
        />
      ))}
      <FormReviewUnmappedSection fields={unmappedFields} />
    </Box>
  );
};

FormReviewSurfaceContent.propTypes = {
  setIsSavingAllowed: PropTypes.func.isRequired,
  additionalProps: PropTypes.shape({
    form: PropTypes.object,
    isInFlight: PropTypes.bool,
    onOpenDocument: PropTypes.func,
  }).isRequired,
};

export default memo(FormReviewSurfaceContent);
